/**
 * Pre-order functionality for BFCM landing page (Zipify Pages)
 * Fetches products based on handles from global settings,
 * extracts preorder-enabled variants, and updates button labels
 */
(function() {
  'use strict';

  // Cache for fetched product data
  const productCache = new Map();
  
  // Map of variant IDs to preorder status
  const preorderVariants = new Map();
  
  // Cache for variant availability to avoid duplicate requests
  const variantAvailabilityCache = new Map();
  
  // Request queue for rate limiting variant fetches
  const variantRequestQueue = [];
  let isProcessingQueue = false;
  const MAX_CONCURRENT_REQUESTS = 3; // Limit concurrent requests
  const REQUEST_DELAY = 100; // Delay between request batches (ms)

  // Set of product handles to process (from global theme settings)
  let allowedHandles = new Set();

  // Button selectors for Zipify Pages
  const BUTTON_SELECTORS = [
      'button[name="add"]',
      'button[type="submit"]',
    '.zpa-add-to-cart-btn',
    '.zp-button',
    '.zpa-button',
    '[data-add-to-cart] button'
  ].join(', ');

  /**
   * Read product handles from global theme settings
   */
  function loadProductHandles() {
    
    // Try to get handles from multiple sources
    let handlesText = null;
    
    // First, try body data attribute (most reliable)
    if (document.body && document.body.getAttribute('data-preorder-handles')) {
      handlesText = document.body.getAttribute('data-preorder-handles');
    }
    // Fallback to window variable
    else if (typeof window.preorderProductHandles !== 'undefined' && window.preorderProductHandles !== null && window.preorderProductHandles !== '') {
      handlesText = window.preorderProductHandles;
    }
    
    if (handlesText && typeof handlesText === 'string' && handlesText.trim()) {
      try {
        // Parse comma-separated handles
        const handles = handlesText.split(',').map(h => h.trim().toLowerCase()).filter(h => h);
        handles.forEach(handle => {
          allowedHandles.add(handle);
        });
      } catch (error) {
        console.warn('Preorder handles parse error', error);
      }
    } else {
      return false;
    }
    
    return allowedHandles.size > 0;
  }

  /**
   * Fetch product data from Shopify Storefront GraphQL API (includes metafields)
   */
  async function fetchProductData(handle) {
    
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    // Use GraphQL Storefront API to get product with variant metafields
    const graphqlQuery = `
      query getProduct($handle: String!) {
        product(handle: $handle) {
          id
          title
          handle
          variants(first: 250) {
            edges {
              node {
                id
                title
                availableForSale
                metafields(first: 50) {
                  edges {
                    node {
                      namespace
                      key
                      value
                      type
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      // Try Storefront GraphQL API
      // Note: This endpoint might require authentication or a different path
      // If this fails, it will fall back to JSON API
      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: graphqlQuery,
          variables: { handle: handle }
        })
      });

      
      if (!response.ok) {
        const errorText = await response.text();
        // Fallback to JSON API
        return await fetchProductDataJSON(handle);
      }
      
      const graphqlData = await response.json();
      console.log('GraphQL fetch result for product:', handle, graphqlData);
      
      if (graphqlData.errors) {
        console.log('GraphQL errors:', graphqlData.errors);
        // Fallback to JSON API
        return await fetchProductDataJSON(handle);
      }
      
      const product = graphqlData.data?.product;
      if (!product) {
        console.log('No product data in GraphQL response');
        return await fetchProductDataJSON(handle);
      }
      
      console.log('GraphQL product data:', product);
      

      // Transform GraphQL response to match expected format
      const productData = {
        id: product.id,
        title: product.title,
        handle: product.handle,
        variants: product.variants.edges.map((edge, index) => {
          const variant = edge.node;
          // Transform metafields from GraphQL format to expected format
          const metafields = {};
          const customMetafields = {};
          
          if (variant.metafields && variant.metafields.edges) {
            
            variant.metafields.edges.forEach(metaEdge => {
              const meta = metaEdge.node;
              
              if (meta.namespace === 'custom') {
                customMetafields[meta.key] = meta.value;
              }
              // Also store in flat structure
              metafields[`${meta.namespace}.${meta.key}`] = meta.value;
            });
          }
          
          if (Object.keys(customMetafields).length > 0) {
            metafields.custom = customMetafields;
          }
          
          // Extract promo_text from metafields (for preorder variants)
          let promoText = null;
          if (customMetafields.promo_text) {
            promoText = typeof customMetafields.promo_text === 'string' 
              ? customMetafields.promo_text 
              : customMetafields.promo_text.value || customMetafields.promo_text;
          } else if (metafields['custom.promo_text']) {
            promoText = typeof metafields['custom.promo_text'] === 'string'
              ? metafields['custom.promo_text']
              : metafields['custom.promo_text'].value || metafields['custom.promo_text'];
          }
          
          const variantId = parseInt(variant.id.split('/').pop());
          const transformedVariant = {
            id: variantId,
            title: variant.title,
            available: variant.availableForSale,
            metafields: metafields,
            promo_text: promoText
          };
          
          console.log('GraphQL variant transformed:', {
            variantId: variantId,
            title: variant.title,
            available: variant.availableForSale,
            promo_text: promoText,
            customMetafields: customMetafields,
            allMetafields: metafields
          });
          
          return transformedVariant;
        })
      };
      
      console.log('GraphQL transformed product data:', productData);
      productCache.set(handle, productData);
      return productData;
    } catch (error) {
      console.error(`Error fetching product ${handle} via GraphQL:`, error);
      // Fallback to JSON API
      return await fetchProductDataJSON(handle);
    }
  }

  /**
   * Fallback: Fetch product data from Shopify JSON API
   */
  async function fetchProductDataJSON(handle) {
    const url = `/products/${handle}.js`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`Failed to fetch product ${handle}:`, response.status);
        return null;
      }
      
      const productData = await response.json();
      
      productCache.set(handle, productData);
      return productData;
    } catch (error) {
      console.error(`Error fetching product ${handle}:`, error);
      return null;
    }
  }

  /**
   * Check if variant has preorder enabled
   */
  function isPreorderVariant(variant) {
    if (!variant) {
      return false;
    }
    
    // Check if metafields exist
    if (!variant.metafields) {
      return false;
    }
    
    // Try different possible structures for metafields
    let preorderEnabled = null;
    
    // Structure 1: variant.metafields.custom.preorder_enabled
    if (variant.metafields.custom && variant.metafields.custom.preorder_enabled !== undefined) {
      preorderEnabled = variant.metafields.custom.preorder_enabled;
    }
    // Structure 2: variant.metafields['custom.preorder_enabled']
    else if (variant.metafields['custom.preorder_enabled'] !== undefined) {
      preorderEnabled = variant.metafields['custom.preorder_enabled'];
    }
    // Structure 3: Check if metafields is an array
    else if (Array.isArray(variant.metafields)) {
      const preorderMeta = variant.metafields.find(m => 
        m.namespace === 'custom' && m.key === 'preorder_enabled'
      );
      if (preorderMeta) {
        preorderEnabled = preorderMeta.value;
      }
    }
    
    if (preorderEnabled === null || preorderEnabled === undefined) {
      return false;
    }
    
    // Handle boolean, string, or value object
    if (preorderEnabled === true || preorderEnabled === 'true' || preorderEnabled === '1') {
      return true;
    }
    
    if (preorderEnabled && typeof preorderEnabled === 'object' && preorderEnabled.value === true) {
      return true;
    }
    
    return false;
  }

  /**
   * Get button label from variant metafield or default
   */
  function getPreorderButtonLabel(variant) {
    if (!variant || !variant.metafields) {
      return 'Pre-order now';
    }
    
    let label = null;
    
    // Try different possible structures for metafields
    if (variant.metafields.custom && variant.metafields.custom.preorder_button_label !== undefined) {
      label = variant.metafields.custom.preorder_button_label;
    }
    else if (variant.metafields['custom.preorder_button_label'] !== undefined) {
      label = variant.metafields['custom.preorder_button_label'];
    }
    else if (Array.isArray(variant.metafields)) {
      const labelMeta = variant.metafields.find(m => 
        m.namespace === 'custom' && m.key === 'preorder_button_label'
      );
      if (labelMeta) {
        label = labelMeta.value;
      }
    }
    
    if (label) {
      return typeof label === 'object' ? label.value : label;
    }
    
    return 'Pre-order now';
  }

  /**
   * Wait for window.preorderVariantMetafields to be available (injected by Liquid)
   * Returns true if data is available, false after max retries
   */
  async function waitForLiquidData(maxRetries = 10, delay = 100) {
    for (let i = 0; i < maxRetries; i++) {
      if (window.preorderVariantMetafields && Object.keys(window.preorderVariantMetafields).length > 0) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    return false;
  }

  /**
   * Fetch all products based on handles and extract preorder-enabled variants
   */
  async function fetchProductsAndExtractPreorderVariants() {
    if (allowedHandles.size === 0) {
      return;
    }

    // Wait for Liquid-injected data to be available (preferred method)
    const hasLiquidData = await waitForLiquidData(10, 100);
    
    // First, check if variant metafields were injected from Liquid (preferred method)
    if (hasLiquidData && window.preorderVariantMetafields && Object.keys(window.preorderVariantMetafields).length > 0) {   
      Object.values(window.preorderVariantMetafields).forEach(variantData => {
        // CRITICAL VALIDATION: only include variants where preorder_enabled is EXACTLY true
        // Exclude: null, undefined, false, 0, '', empty string, empty object, 'false', etc.
        const isPreorderEnabled = (
          variantData.preorder_enabled === true || 
          variantData.preorder_enabled === 'true' ||
          variantData.preorder_enabled === 1 ||
          (variantData.preorder_enabled && 
           typeof variantData.preorder_enabled === 'object' && 
           variantData.preorder_enabled.value === true)
        ) && (
          variantData.preorder_enabled !== false &&
          variantData.preorder_enabled !== 'false' &&
          variantData.preorder_enabled !== 0 &&
          variantData.preorder_enabled !== '' &&
          variantData.preorder_enabled !== null &&
          variantData.preorder_enabled !== undefined
        );
        
        if (!isPreorderEnabled) {
          return;
        }

        // Store variant ID in multiple formats to ensure matching
        const variantId = variantData.id.toString();
        const variantIdNum = parseInt(variantData.id, 10);
        const preorderData = {
          variant: {
            id: variantData.id,
            title: variantData.title,
            available: variantData.available,
            promo_text: variantData.promo_text || null
          },
          product: {
            handle: variantData.product_handle
          },
          available: variantData.available,
          label: variantData.preorder_button_label || 'Pre-order now',
          promo_text: variantData.promo_text || null
        };
        // Store with both string formats to ensure we can find it
        preorderVariants.set(variantId, preorderData);
        if (!isNaN(variantIdNum)) {
          preorderVariants.set(variantIdNum.toString(), preorderData);
        }
      });
      
      return;
    }

    // Fallback: Fetch products via API (variant metafields won't be available)
    const handlesArray = Array.from(allowedHandles);
    
    // Fetch all products in parallel
    const fetchPromises = handlesArray.map(handle => {
      return fetchProductData(handle);
    });
    
    const products = await Promise.all(fetchPromises);
    
    // Extract preorder-enabled variants from all products
    products.forEach(product => {
      if (!product || !product.variants) {
        return;
      }
      
      product.variants.forEach((variant) => {
        // Store variant ID in multiple formats to ensure matching
        const variantId = variant.id.toString();
        const variantIdNum = parseInt(variant.id, 10);
        
        // Check if preorder enabled
        const isPreorder = isPreorderVariant(variant);
        
        if (isPreorder) {
          const preorderLabel = getPreorderButtonLabel(variant);
          // Get promo_text from variant (already extracted from GraphQL or API)
          const promoText = variant.promo_text || getPromoText(variant);
          const preorderData = {
            variant: variant,
            product: product,
            available: variant.available,
            label: preorderLabel,
            promo_text: promoText
          };
          // Store with both string and number keys to ensure we can find it
          preorderVariants.set(variantId, preorderData);
          if (!isNaN(variantIdNum)) {
            preorderVariants.set(variantIdNum.toString(), preorderData);
          }
        }
      });
    });
  }

  /**
   * Process variant request queue with rate limiting
   */
  async function processVariantRequestQueue() {
    if (isProcessingQueue || variantRequestQueue.length === 0) {
      return;
    }
    
    isProcessingQueue = true;
    
    while (variantRequestQueue.length > 0) {
      const batch = variantRequestQueue.splice(0, MAX_CONCURRENT_REQUESTS);
      const promises = batch.map(({ variantId, button, resolve, reject }) => 
        fetchVariantAvailabilityInternal(variantId, button)
          .then(resolve)
          .catch(reject)
      );
      
      await Promise.allSettled(promises);
      
      // Delay before next batch
      if (variantRequestQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, REQUEST_DELAY));
      }
    }
    
    isProcessingQueue = false;
  }

  /**
   * Fetch variant availability from backend (with caching and rate limiting)
   */
  async function fetchVariantAvailability(variantId, button) {
    const variantIdStr = variantId.toString();
    
    // Check cache first
    if (variantAvailabilityCache.has(variantIdStr)) {
      return variantAvailabilityCache.get(variantIdStr);
    }
    
    // Return a promise that will be resolved when the request is processed
    return new Promise((resolve, reject) => {
      variantRequestQueue.push({ variantId: variantIdStr, button, resolve, reject });
      // Trigger queue processing if not already running
      processVariantRequestQueue();
    });
  }

  /**
   * Internal function to actually fetch variant availability
   */
  async function fetchVariantAvailabilityInternal(variantId, button) {
    const variantIdStr = variantId.toString();
    
    // Double-check cache (might have been added by another request)
    if (variantAvailabilityCache.has(variantIdStr)) {
      return variantAvailabilityCache.get(variantIdStr);
    }
    
    // Try to get product handle from button context
    let productHandle = getProductHandle(button);
    
    // If we have product handle, fetch product data (this is preferred as it's more efficient)
    if (productHandle) {
      try {
        const productData = await fetchProductData(productHandle);
        if (productData && productData.variants) {
          const variant = productData.variants.find(v => {
            const vid = v.id.toString();
            const targetId = variantIdStr;
            return vid === targetId;
          });
          if (variant) {
            const available = variant.available !== false && 
                             variant.available !== null && 
                             variant.available !== undefined;
            const result = {
              available: available,
              variant: variant
            };
            // Cache the result
            variantAvailabilityCache.set(variantIdStr, result);
            return result;
          }
        }
      } catch (error) {
        // Silently fail and try fallback - don't spam console
        // console.error('Error fetching variant availability from product:', error);
      }
    }
    
    // Fallback: Try to fetch variant data directly from Shopify variant endpoint
    // Only if we don't have product handle or product fetch failed
    try {
      const variantIdNum = parseInt(variantIdStr, 10);
      if (!isNaN(variantIdNum)) {
        // Try fetching from variant JSON endpoint
        const response = await fetch(`/variants/${variantIdNum}.js`);
        if (response.ok) {
          const variantData = await response.json();
          const available = variantData.available !== false && 
                           variantData.available !== null && 
                           variantData.available !== undefined;
          const result = {
            available: available,
            variant: variantData
          };
          // Cache the result
          variantAvailabilityCache.set(variantIdStr, result);
          return result;
        }
      }
    } catch (error) {
      // Only log if it's not a resource error (to avoid spam)
      if (!error.message || !error.message.includes('Failed to fetch') || !error.message.includes('ERR_INSUFFICIENT_RESOURCES')) {
        // console.error('Error fetching variant data from endpoint:', error);
      }
      // Return null to indicate failure, but don't cache failures
      return null;
    }
    
    return null;
  }

  /**
   * Extract product handle from various sources
   */
  function getProductHandle(element) {
    // Method 1: Check form action URL
    const form = element.closest('form');
    if (form && form.action) {
      const match = form.action.match(/\/products\/([^\/\?]+)/);
      if (match) return match[1].toLowerCase();
    }

    // Method 2: Check product link in card/container
    const card = element.closest('.zpa-flex-column, .zp-product-card, .zp-product-block, [data-product-handle], .zpa-product-block, .zp-product-wrapper');
    if (card) {
      const productLink = card.querySelector('a[href*="/products/"]:not([href*="/collections/"])');
      if (productLink) {
        const match = productLink.href.match(/\/products\/([^\/\?]+)/);
        if (match) return match[1].toLowerCase();
      }

      // Check data attributes on card
      const handle = card.getAttribute('data-product-handle') || 
                     card.getAttribute('data-product') || 
                     card.getAttribute('data-handle') ||
                     card.getAttribute('data-zp-product-handle');
      if (handle) return handle.toLowerCase();
    }

    // Method 3: Check data attributes on element or parents (more levels)
    let current = element;
    for (let i = 0; i < 15 && current; i++) {
      const handle = current.getAttribute('data-product-handle') || 
                     current.getAttribute('data-product') || 
                     current.getAttribute('data-handle') ||
                     current.getAttribute('data-zp-product-handle');
      if (handle) return handle.toLowerCase();
      current = current.parentElement;
    }

    // Method 4: Try to get from variant ID if we have it
    const variantId = getVariantId(element);
    if (variantId) {
      const preorderData = preorderVariants.get(variantId.toString());
      if (preorderData && preorderData.product && preorderData.product.handle) {
        return preorderData.product.handle.toLowerCase();
      }
    }

    return null;
  }

  /**
   * Get current variant ID from form or Zipify context
   * This function dynamically retrieves the variant ID from multiple sources
   */
  function getVariantId(element) {
    // Method 1: Check button's own data attributes
    const buttonVariantId = element.getAttribute('data-variant-id') ||
                           element.getAttribute('data-zp-variant-id') ||
                           element.getAttribute('data-product-variant-id') ||
                           element.getAttribute('data-variant');
    if (buttonVariantId) {
      return buttonVariantId;
    }

    // Method 2: Check form first (most reliable for dynamic updates)
    const form = element.closest('form');
    if (form) {
      // Check hidden input with name="id" (most common)
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }

      // Check select dropdown with name="id"
      const variantSelect = form.querySelector('select[name="id"]');
      if (variantSelect && variantSelect.value) {
        return variantSelect.value;
      }

      // Check all hidden inputs in form
      const allHiddenInputs = form.querySelectorAll('input[type="hidden"]');
      for (const input of allHiddenInputs) {
        if (input.value && (input.name === 'id' || input.name === 'variant_id' || input.name.includes('variant'))) {
          return input.value;
        }
      }

      // Check for variant-selects custom element (Shopify theme)
      const variantSelects = form.querySelector('variant-selects');
      if (variantSelects) {
        const variantId = variantSelects.getAttribute('data-selected-variant-id') ||
                         variantSelects.querySelector('input[name="id"]')?.value;
        if (variantId) {
          return variantId;
        }
      }

      // Check form action URL for variant ID
      if (form.action) {
        const variantMatch = form.action.match(/[?&]variant=(\d+)/);
        if (variantMatch) {
          return variantMatch[1];
        }
      }
    }

    // Method 3: Check Zipify product context
    const zipifyContext = element.closest('[data-zp-product-form]') || 
                         element.closest('.zpa-product-form') ||
                         element.closest('[data-product-handle]') ||
                         element.closest('.zpa-product-block');
    
    if (zipifyContext) {
      // Check data attributes on context
      const zpVariantId = zipifyContext.getAttribute('data-variant-id') || 
                          zipifyContext.getAttribute('data-selected-variant-id') ||
                          zipifyContext.querySelector('[data-variant-id]')?.getAttribute('data-variant-id');
      if (zpVariantId) {
        return zpVariantId;
      }

      // Check for hidden inputs in Zipify context
      const zpForm = zipifyContext.querySelector('form');
      if (zpForm) {
        const zpVariantInput = zpForm.querySelector('input[name="id"]');
        if (zpVariantInput && zpVariantInput.value) {
          return zpVariantInput.value;
        }
      }
    }

    // Method 4: Check parent elements for Zipify data attributes
    let current = element.parentElement;
    for (let i = 0; i < 15 && current; i++) {
      const variantId = current.getAttribute('data-variant-id') ||
                       current.getAttribute('data-zp-variant-id') ||
                       current.getAttribute('data-product-variant-id') ||
                       current.getAttribute('data-variant') ||
                       current.getAttribute('data-selected-variant-id');
      
      if (variantId) {
        return variantId;
      }
      
      // Check for hidden inputs in this element
      const hiddenInputs = current.querySelectorAll('input[type="hidden"]');
      for (const input of hiddenInputs) {
        if (input.name === 'id' || input.name === 'variant_id' || input.name.includes('variant')) {
          if (input.value) {
            return input.value;
          }
        }
      }
      
      current = current.parentElement;
    }

    return null;
  }

  /**
   * Get promo text from variant metafields
   */
  function getPromoText(variantData) {
    if (!variantData) return null;
    
    // Check if variantData itself has promo_text (direct property)
    if (variantData.promo_text) {
      return typeof variantData.promo_text === 'string' ? variantData.promo_text : variantData.promo_text.value || variantData.promo_text;
    }
    
    // Check if variantData has metafields
    if (variantData.metafields) {
      // Try different possible structures
      if (variantData.metafields.custom && variantData.metafields.custom.promo_text) {
        const promoText = variantData.metafields.custom.promo_text;
        return typeof promoText === 'string' ? promoText : promoText.value || promoText;
      }
      if (variantData.metafields['custom.promo_text']) {
        const promoText = variantData.metafields['custom.promo_text'];
        return typeof promoText === 'string' ? promoText : promoText.value || promoText;
      }
      // Check if metafields is an array
      if (Array.isArray(variantData.metafields)) {
        const promoMeta = variantData.metafields.find(m => 
          m.namespace === 'custom' && m.key === 'promo_text'
        );
        if (promoMeta) {
          return typeof promoMeta.value === 'string' ? promoMeta.value : promoMeta.value?.value || promoMeta.value;
        }
      }
    }
    
    return null;
  }

  /**
   * Update promo text above the button
   */
  function updatePromoText(button, promoText) {
    // Find button container - try multiple selectors for different layouts
    const form = button.closest('form');
    let buttonContainer = button.closest('.product-form__buttons') || 
                         button.closest('.zpa-product-form') ||
                         button.closest('.product-form') ||
                         button.closest('[data-zp-product-form]') ||
                         button.parentElement;
    
    if (!buttonContainer) return;
    
    // Look for existing promo text element
    let promoElement = buttonContainer.querySelector('.variant-promo-text');
    
    if (promoText && promoText.trim()) {
      // Create or update promo text element
      if (!promoElement) {
        promoElement = document.createElement('div');
        promoElement.className = 'variant-promo-text';
        promoElement.style.cssText = 'margin-bottom: 1rem; text-align: center; font-size: 0.9rem; color: inherit; line-height: 1.4;';
        // Try to insert before button, or before button container
        if (button.parentNode) {
          button.parentNode.insertBefore(promoElement, button);
        } else if (buttonContainer) {
          buttonContainer.insertBefore(promoElement, buttonContainer.firstChild);
        }
      }
      promoElement.textContent = promoText.trim();
      promoElement.style.display = '';
    } else {
      // Hide promo text if empty
      if (promoElement) {
        promoElement.style.display = 'none';
      }
    }
  }

  /**
   * Update button text and status based on variant
   */
  function updateButton(button, text, disabled) {
    const zipifyTextSpan = button.querySelector('[data-zp-add-to-cart-text]') || 
                          button.querySelector('.zpa-btn-custom__caption') ||
                          button.querySelector('span.zpa-btn-custom__caption') ||
                          button.querySelector('span');
    
    // Mark elements as being updated by us to prevent observer loops
    const targetElement = zipifyTextSpan || button;
    targetElement._preorderUpdating = true;
    
    // Store expected text on button for watcher
    button.dataset.expectedText = text;
    
    // Update text in ALL possible places
    if (zipifyTextSpan) {
      zipifyTextSpan.textContent = text;
      zipifyTextSpan.innerText = text;
      zipifyTextSpan.innerHTML = text;
      if (zipifyTextSpan.hasAttribute('data-zp-add-to-cart-text')) {
        zipifyTextSpan.setAttribute('data-zp-add-to-cart-text', text);
      }
    }
    
    // Always update button directly too
    button.textContent = text;
    button.innerText = text;
    button.innerHTML = text;
    button.setAttribute('data-zp-add-to-cart-text', text);
    button.setAttribute('aria-label', text);
    
    // Update disabled status
    if (disabled) {
      button.disabled = true;
      button.setAttribute('disabled', 'disabled');
    } else {
      button.disabled = false;
      button.removeAttribute('disabled');
    }
    
    // Use double requestAnimationFrame to re-apply after Zipify updates
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Re-check and re-apply if Zipify overwrote it
        const currentText = (zipifyTextSpan || button).textContent.trim();
        if (currentText !== text && currentText !== 'Pre-order limit reached' && currentText !== 'Pre-order now') {
          // Zipify overwrote it, re-apply
          if (zipifyTextSpan) {
            zipifyTextSpan.textContent = text;
            zipifyTextSpan.innerText = text;
            zipifyTextSpan.innerHTML = text;
            if (zipifyTextSpan.hasAttribute('data-zp-add-to-cart-text')) {
              zipifyTextSpan.setAttribute('data-zp-add-to-cart-text', text);
            }
          }
          button.textContent = text;
          button.innerText = text;
          button.innerHTML = text;
          button.setAttribute('data-zp-add-to-cart-text', text);
        }
        targetElement._preorderUpdating = false;
      });
    });
  }

  /**
   * Process a single add-to-cart button
   * CRITICAL: This function ONLY processes variants when we have an EXACT variant ID match.
   * We NEVER assume a variant is preorder based on product-level checks.
   */
  function processButton(button) {
    // Store original text early if not already stored (before any preorder processing)
    // This ensures we capture the default "Add to Cart" text, not preorder text
    if (!button.dataset.originalText) {
      const zipifyTextSpan = button.querySelector('[data-zp-add-to-cart-text]') || 
                            button.querySelector('.zpa-btn-custom__caption') ||
                            button.querySelector('span.zpa-btn-custom__caption') ||
                            button.querySelector('span');
      const textElement = zipifyTextSpan || button;
      const currentText = textElement.textContent.trim();
      
      // Check if current text is a preorder text
      const isPreorderText = currentText === 'Pre-order now' || 
                            currentText === 'Pre-order limit reached' ||
                            currentText.toLowerCase().includes('pre-order');
      
      if (!isPreorderText && currentText) {
        // Store the current text as original (it's the default)
        button.dataset.originalText = currentText;
      } else {
        // Current text is preorder text, try to get default from Zipify
        // Check data attributes for default text
        const dataText = zipifyTextSpan?.getAttribute('data-zp-add-to-cart-text') || 
                        button.getAttribute('data-zp-add-to-cart-text') ||
                        button.getAttribute('data-original-text') ||
                        'ADD TO CART';
        
        // Only use if it's not a preorder text
        if (dataText && 
            dataText !== 'Pre-order now' && 
            dataText !== 'Pre-order limit reached' &&
            !dataText.toLowerCase().includes('pre-order')) {
          button.dataset.originalText = dataText;
        } else {
          // Fallback to default
          button.dataset.originalText = 'ADD TO CART';
        }
      }
    }
    
    // First, try to get variant ID (this is the most important)
    let variantId = getVariantId(button);
    
    // CRITICAL: We must have an EXACT variant ID to proceed
    // No product-level assumptions allowed!
    if (!variantId) {
      // If no variant ID, restore to default state
      if (button.dataset.lastVariantId) {
        const originalText = button.dataset.originalText || 'ADD TO CART';
        updateButton(button, originalText, false);
        updatePromoText(button, null);
        delete button.dataset.lastVariantId;
        delete button.dataset.lastProcessedStatus;
      }
      return;
    }
    
    const variantIdStr = variantId.toString().trim();
    const variantIdNum = parseInt(variantIdStr, 10);
    
    // Check if this EXACT variant ID is in our preorder map (try multiple formats)
    let preorderData = preorderVariants.get(variantIdStr);
    
    // Try alternative formats if not found
    if (!preorderData && !isNaN(variantIdNum)) {
      preorderData = preorderVariants.get(variantIdNum.toString());
    }
    if (!preorderData && variantIdStr) {
      // Try without any whitespace or extra characters
      preorderData = preorderVariants.get(variantIdStr.replace(/\s+/g, ''));
    }
    
    // Log variant change
    if (button.dataset.lastVariantId !== variantIdStr) {
      // Debug: log all preorder variant IDs to help troubleshoot if not found
      if (!preorderData && preorderVariants.size > 0) {
        const allKeys = Array.from(preorderVariants.keys());
      }
    }
    
    // Skip if same variant already processed (but allow reprocessing if status changed)
    // Only skip if it's the same variant AND we've already processed it with the same status
    const lastStatus = button.dataset.lastProcessedStatus;
    if (button.dataset.lastVariantId === variantIdStr && lastStatus) {
      // For non-preorder variants, always reprocess to check availability changes
      if (lastStatus === 'non-preorder') {
        // Clear status to force reprocessing
        delete button.dataset.lastProcessedStatus;
      } else {
        return;
      }
    }
    
    // Clear any previous observer
    if (button._preorderObserver) {
      button._preorderObserver.disconnect();
      delete button._preorderObserver;
    }
    
    if (preorderData) {
      // Preorder variant
      const productHandle = preorderData.product.handle;
      
      // Check if handle is in allowed list (if list is configured)
      if (allowedHandles.size > 0 && !allowedHandles.has(productHandle.toLowerCase())) {
        return;
      }
      
      
      // Check if available is explicitly false or falsy (but not 0 as that could be valid)
      const isAvailable = preorderData.available === true || 
                         (preorderData.available !== false && 
                          preorderData.available !== null && 
                          preorderData.available !== undefined &&
                          preorderData.available !== 'false' &&
                          preorderData.available !== 0);
      
      // Get promo text from preorder data (only for preorder variants)
      const promoText = preorderData.promo_text || getPromoText(preorderData.variant);
      
      if (!isAvailable || preorderData.available === false) {
        // available = false: Show "Pre-order limit reached" + disabled
        updateButton(button, 'Pre-order limit reached', true);
        updatePromoText(button, promoText);
      } else {
        // available = true: Show preorder label (or "Pre-order now") + enabled
        const text = preorderData.label || 'Pre-order now';
        updateButton(button, text, false);
        updatePromoText(button, promoText);
      }
      
      button.dataset.lastVariantId = variantIdStr;
      button.dataset.lastProcessedStatus = 'preorder';
      return;
    } else {
      // Non-preorder variant
      // Store variant ID first to track it
      button.dataset.lastVariantId = variantIdStr;
      
      // For non-preorder variants, fetch availability from backend
      (async () => {
        // Only process if variant hasn't changed
        if (button.dataset.lastVariantId !== variantIdStr) {
          return;
        }

        try {
          const variantData = await fetchVariantAvailability(variantIdStr, button);
          
          // Check if variant changed while we were fetching
          if (button.dataset.lastVariantId !== variantIdStr) {
            return;
          }
          
          if (variantData) {
            const available = variantData.available;
            
            // Non-preorder variants don't show promo text
            if (!available) {
              // available = false: Show "Sold out" + disabled
              updateButton(button, 'Sold out', true);
              updatePromoText(button, null);
            } else {
              // available = true: Show "Add to Cart" + enabled
              const originalText = button.dataset.originalText || 'ADD TO CART';
              updateButton(button, originalText, false);
              updatePromoText(button, null);
            }
            
            button.dataset.lastProcessedStatus = 'non-preorder';
          } else {
            // Could not fetch from backend, fallback to DOM check
            
            const variantSelects = button.closest('form')?.querySelector('variant-selects');
            let available = true; // Default to available
            
            if (variantSelects && variantSelects.currentVariant) {
              const currentVariantId = variantSelects.currentVariant.id?.toString();
              if (currentVariantId === variantIdStr) {
                available = variantSelects.currentVariant.available !== false && 
                           variantSelects.currentVariant.available !== null &&
                           variantSelects.currentVariant.available !== undefined;
              }
            }
            
            // Non-preorder variants don't show promo text
            if (!available) {
              updateButton(button, 'Sold out', true);
              updatePromoText(button, null);
            } else {
              const originalText = button.dataset.originalText || 'ADD TO CART';
              updateButton(button, originalText, false);
              updatePromoText(button, null);
            }
            
            button.dataset.lastProcessedStatus = 'non-preorder';
          }
        } catch (error) {
          // Only log if it's not a resource limit error (to avoid console spam)
          if (!error.message || (!error.message.includes('Failed to fetch') && !error.message.includes('ERR_INSUFFICIENT_RESOURCES'))) {
            // console.error('Error fetching variant availability:', error);
          }
          // On error, default to available
          const originalText = button.dataset.originalText || 'ADD TO CART';
          updateButton(button, originalText, false);
          updatePromoText(button, null);
          button.dataset.lastProcessedStatus = 'non-preorder';
        }
      })();
      
      return;
    }
  }
  

  /**
   * Scan page for add-to-cart buttons and process them
   */
  function scanAndUpdateButtons() {
    const buttons = document.querySelectorAll(BUTTON_SELECTORS);
    
    buttons.forEach((button) => {
      // Check if button is in a form or has Zipify product context
      const form = button.closest('form');
      const zipifyContext = button.closest('[data-zp-product-form]') || 
                           button.closest('.zpa-product-form') ||
                           button.closest('[data-product-handle]');
      
      if (!form && !zipifyContext) {
        return;
      }
      
      processButton(button);
    });
  }

  /**
   * Handle variant selector changes
   */
  function setupVariantListeners() {
    // Listen for change events on variant inputs directly
    document.addEventListener('change', function(e) {
      const target = e.target;
      
      // Check if this is a variant ID input (most direct)
      if (target.name === 'id' || target.name === 'variant_id') {
        const form = target.closest('form');
        if (form) {
          const button = form.querySelector(BUTTON_SELECTORS);
          if (button) {
            delete button.dataset.lastVariantId;
            delete button.dataset.lastProcessedStatus;
            processButton(button);
            return;
          }
        }
      }
      
      // Check if this is a variant selector (various patterns)
      const isVariantSelector = target.name === 'id' || 
                                target.matches('select[name*="option"], input[name*="option"]') ||
                                target.classList.contains('variant-selector') ||
                                target.hasAttribute('data-zp-variant-selector') ||
                                target.closest('[data-zp-variant-selector]');
      
      if (!isVariantSelector) {
        return;
      }
      
      // Find the button in form or Zipify context
      const form = target.closest('form');
      const zipifyContext = target.closest('[data-zp-product-form]') || 
                           target.closest('.zpa-product-form') ||
                           target.closest('[data-product-handle]');
      
      const context = form || zipifyContext;
      if (!context) {
        return;
      }

      const button = context.querySelector(BUTTON_SELECTORS);
      if (!button) {
        return;
      }

      // Process immediately - variant ID should be updated by now
      delete button.dataset.lastVariantId;
      delete button.dataset.lastProcessedStatus;
      processButton(button);
    });

    // Listen for input events on variant ID inputs (catches programmatic changes)
    document.addEventListener('input', function(e) {
      const target = e.target;
      
      // Direct variant ID input change
      if (target.name === 'id' || target.name === 'variant_id') {
        const form = target.closest('form');
        if (form) {
          const button = form.querySelector(BUTTON_SELECTORS);
          if (button) {
            delete button.dataset.lastVariantId;
            delete button.dataset.lastProcessedStatus;
            processButton(button);
            return;
          }
        }
      }
      
      // Option selectors (for variant pickers)
      if (target.name && target.name.includes('option')) {
        const form = target.closest('form');
        const zipifyContext = target.closest('[data-zp-product-form]') || 
                             target.closest('.zpa-product-form');
        const context = form || zipifyContext;
        if (!context) return;

        const button = context.querySelector(BUTTON_SELECTORS);
        if (!button) return;

        // Process immediately - variant ID should be updated by now
        delete button.dataset.lastVariantId;
        delete button.dataset.lastProcessedStatus;
        processButton(button);
      }
    });

    // Listen for Zipify-specific events if they exist
    if (window.ZipifyPages) {
      // Listen for Zipify variant change events
      document.addEventListener('zp:variant:change', function(e) {
        scanAndUpdateButtons();
      });
    }

    // Also listen for any custom events that might indicate variant changes
    ['variant:change', 'product:variant:change', 'zp:variant:selected'].forEach(eventName => {
      document.addEventListener(eventName, function(e) {
        scanAndUpdateButtons();
      });
    });
  }

  /**
   * Initialize
   */
  async function init() {
    
    // Wait for body to be available if needed
    if (!document.body) {
      await new Promise(resolve => {
        if (document.body) {
          resolve();
        } else {
          const observer = new MutationObserver(() => {
            if (document.body) {
              observer.disconnect();
              resolve();
            }
          });
          observer.observe(document.documentElement, { childList: true });
        }
      });
    }
    
    // Step 1: Load product handles from global theme settings
    const hasHandles = loadProductHandles();
    
    if (!hasHandles) {
      return;
    }

    // Step 2: Fetch all products and extract preorder-enabled variants
    await fetchProductsAndExtractPreorderVariants();

    // Step 3: Wait for DOM to be ready, then scan and update buttons
    const processButtons = () => {
      scanAndUpdateButtons();
      setupVariantListeners();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Small delay to ensure all scripts have run
        setTimeout(processButtons, 50);
      });
    } else {
      // If DOM is already ready, wait a bit for other scripts to initialize
      setTimeout(processButtons, 50);
    }

    // Step 4: Watch for window.preorderVariantMetafields to become available
    // (in case Liquid script loads after our initial check)
    if (!window.preorderVariantMetafields || Object.keys(window.preorderVariantMetafields).length === 0) {
      let checkCount = 0;
      const maxChecks = 20; // Check for up to 2 seconds (20 * 100ms)
      const checkInterval = setInterval(() => {
        checkCount++;
        if (window.preorderVariantMetafields && Object.keys(window.preorderVariantMetafields).length > 0) {
          clearInterval(checkInterval);
          // Re-fetch and re-scan when data becomes available
          fetchProductsAndExtractPreorderVariants().then(() => {
            scanAndUpdateButtons();
          });
        } else if (checkCount >= maxChecks) {
          clearInterval(checkInterval);
        }
      }, 100);
    }

    // Watch for dynamically added content (Zipify lazy-loading)
    if ('MutationObserver' in window) {
      // Flag to prevent infinite loops
      let isProcessingTextChanges = false;
      
      // Debounce timer for observer
      let observerDebounceTimer = null;
      const OBSERVER_DEBOUNCE_DELAY = 300; // Wait 300ms after last mutation before processing
      
      const observer = new MutationObserver(function(mutations) {
        // Clear existing timer
        if (observerDebounceTimer) {
          clearTimeout(observerDebounceTimer);
        }
        
        // Debounce: only process after mutations stop for 300ms
        observerDebounceTimer = setTimeout(() => {
          // Prevent re-entrant calls
          if (isProcessingTextChanges) {
            return;
          }
          
          let shouldScan = false;
          let buttonTextChanged = false;
        
        mutations.forEach(function(mutation) {
          // Skip mutations caused by our own updates
          if (mutation.target && mutation.target._preorderUpdating) {
            return;
          }
          
          if (mutation.addedNodes.length) {
            // Check if any added nodes contain buttons or forms
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) { // Element node
                if (node.matches && (node.matches(BUTTON_SELECTORS) || node.querySelector(BUTTON_SELECTORS))) {
                  shouldScan = true;
                }
                if (node.matches && node.matches('form') || node.querySelector && node.querySelector('form')) {
                  shouldScan = true;
                }
              }
            });
          }
          
          // Watch for text changes in button spans (Zipify might update them)
          // But only if the change is not from our own updates
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const target = mutation.target;
            if (target && !target._preorderUpdating && (
              target.matches && (
                target.matches('[data-zp-add-to-cart-text]') ||
                target.matches('.zpa-btn-custom__caption') ||
                target.closest(BUTTON_SELECTORS)
              )
            )) {
              // If button text changed and we have a variant ID, reprocess
              const button = target.closest(BUTTON_SELECTORS) || target;
              if (button && button.dataset.lastVariantId) {
                buttonTextChanged = true;
              }
            }
          }
        });

          if (shouldScan) {
            // Process immediately
            scanAndUpdateButtons();
          }
          
          if (buttonTextChanged) {
            isProcessingTextChanges = true;
            
            // Re-process buttons if text was changed by Zipify
            const buttons = document.querySelectorAll(BUTTON_SELECTORS);
            buttons.forEach(button => {
              if (button.dataset.lastVariantId) {
                // Force reprocess by clearing status
                const variantId = button.dataset.lastVariantId;
                delete button.dataset.lastProcessedStatus;
                processButton(button);
              }
            });
            
            // Clear flag after DOM updates
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                isProcessingTextChanges = false;
              });
            });
          }
        }, OBSERVER_DEBOUNCE_DELAY);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // Watch for variant input changes using MutationObserver (more efficient than intervals)
    // This watches the hidden input[name="id"] for value changes
    if ('MutationObserver' in window) {
      const variantObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const input = mutation.target;
            if (input.name === 'id' || input.name === 'variant_id') {
              const form = input.closest('form');
              if (form) {
                const button = form.querySelector(BUTTON_SELECTORS);
                if (button) {
                  // Variant ID changed, process immediately
                  delete button.dataset.lastVariantId;
                  delete button.dataset.lastProcessedStatus;
                  processButton(button);
                }
              }
            }
          }
        });
      });

      // Observe all variant input fields
      const variantInputs = document.querySelectorAll('input[name="id"], input[name="variant_id"]');
      variantInputs.forEach(input => {
        variantObserver.observe(input, {
          attributes: true,
          attributeFilter: ['value']
        });
      });

      // Also observe newly added inputs
      const inputObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              const newInputs = node.querySelectorAll ? node.querySelectorAll('input[name="id"], input[name="variant_id"]') : [];
              newInputs.forEach(input => {
                variantObserver.observe(input, {
                  attributes: true,
                  attributeFilter: ['value']
                });
              });
              if (node.matches && (node.matches('input[name="id"]') || node.matches('input[name="variant_id"]'))) {
                variantObserver.observe(node, {
                  attributes: true,
                  attributeFilter: ['value']
                });
              }
            }
          });
        });
      });

      inputObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Start
  init();
})();
